import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnchor,
  faCheckCircle,
  faDownload,
  faExclamationTriangle,
  faFileUpload,
  faInfoCircle,
  faPenToSquare,
  faSpinner,
  faTimes,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';

import systemDataService from '../services/systemDataService';
import { useFlashMessage } from './FlashMessage';
import { getErrorMessage } from '../utils/errorMessages';

/*
 * PortBulkUploadModal
 * -------------------
 * Two-stage UI:
 *   1) Upload modal — instructions + CSV template download + file picker
 *   2) Results modal — stat cards + tables of new/updated/error rows
 *
 * Mirrors the structure of the employee bulk-upload modals embedded inside
 * HMSAdminDashboard.js (`.bulk-upload-modal`, `.bulk-upload-results-modal`,
 * `.bulk-upload-*` CSS classes) so it picks up the existing styling without
 * any new CSS.
 */

const EXPECTED_HEADERS = [
  'Port Name',
  'UN/LOCODE',
  'Country Name',
  'Country ISO Code',
  'Latitude',
  'Longitude',
  'Is Bunker Port',
];

const SAMPLE_ROWS = [
  ['Port of Singapore', 'SGSIN', 'Singapore', 'SG', '1.2655', '103.8201', 'true'],
  ['Rotterdam', 'NLRTM', 'Netherlands', 'NL', '51.9244', '4.4777', 'true'],
  ['Houston', 'USHOU', 'United States', 'US', '29.7604', '-95.3698', 'false'],
];

const PortBulkUploadModal = ({ onClose, onUploaded }) => {
  const { showError, showSuccess } = useFlashMessage();
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [results, setResults] = useState(null);

  const handleDownloadTemplate = () => {
    const rows = [EXPECTED_HEADERS, ...SAMPLE_ROWS];
    const csv = rows.map((r) => r.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'HMS_Port_Bulk_Upload_Template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      showError('Please select a CSV file to upload.');
      return;
    }
    setIsUploading(true);
    try {
      const response = await systemDataService.bulkUploadPorts(file);
      setResults(response);
      setFile(null);
      const newCount = response?.statistics?.new_ports || 0;
      const updatedCount = response?.statistics?.updated_ports || 0;
      const errorCount = response?.errors?.length || 0;
      if (errorCount === 0) {
        showSuccess(`Bulk upload complete: ${newCount} created, ${updatedCount} updated.`);
      }
      if (onUploaded) onUploaded();
    } catch (err) {
      showError(getErrorMessage(err, 'Bulk upload failed. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    onClose();
  };

  if (results) {
    return (
      <div className="admin-modal-overlay" onClick={handleClose}>
        <div
          className="modal bulk-upload-results-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>
              <FontAwesomeIcon icon={faAnchor} /> Port Bulk Upload Results
            </h2>
            <button className="close-btn" onClick={handleClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="bulk-upload-results-content">
            <div className="bulk-upload-statistics-grid">
              <div className="bulk-upload-stat-card bulk-upload-stat-card-success">
                <div className="bulk-upload-stat-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div className="bulk-upload-stat-info">
                  <h3>{results.statistics?.new_ports || 0}</h3>
                  <p>New Ports Added</p>
                </div>
              </div>

              <div className="bulk-upload-stat-card bulk-upload-stat-card-update">
                <div className="bulk-upload-stat-icon">
                  <FontAwesomeIcon icon={faPenToSquare} />
                </div>
                <div className="bulk-upload-stat-info">
                  <h3>{results.statistics?.updated_ports || 0}</h3>
                  <p>Ports Updated</p>
                </div>
              </div>

              <div className="bulk-upload-stat-card bulk-upload-stat-card-error">
                <div className="bulk-upload-stat-icon">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </div>
                <div className="bulk-upload-stat-info">
                  <h3>{results.statistics?.unaffected || 0}</h3>
                  <p>Rows With Errors</p>
                </div>
              </div>
            </div>

            {results.new_ports_details && results.new_ports_details.length > 0 && (
              <div className="bulk-upload-details-section bulk-upload-success-section">
                <h3 className="bulk-upload-section-header bulk-upload-success-header">
                  <FontAwesomeIcon icon={faCheckCircle} /> New Ports Added ({results.new_ports_details.length})
                </h3>
                <div className="bulk-upload-table-wrapper">
                  <table className="bulk-upload-details-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>UN/LOCODE</th>
                        <th>Port Name</th>
                        <th>Country</th>
                        <th>ISO</th>
                        <th>Bunker?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.new_ports_details.map((p, idx) => (
                        <tr key={`new-${idx}`}>
                          <td>{p.row}</td>
                          <td>{p.unlocode}</td>
                          <td>{p.port_name}</td>
                          <td>{p.country_name}</td>
                          <td>{p.country_iso_code}</td>
                          <td>{p.is_bunker_port ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {results.updated_ports_details && results.updated_ports_details.length > 0 && (
              <div className="bulk-upload-details-section bulk-upload-update-section">
                <h3 className="bulk-upload-section-header bulk-upload-update-header">
                  <FontAwesomeIcon icon={faPenToSquare} /> Ports Updated ({results.updated_ports_details.length})
                </h3>
                <div className="bulk-upload-table-wrapper">
                  <table className="bulk-upload-details-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>UN/LOCODE</th>
                        <th>Port Name</th>
                        <th>Country</th>
                        <th>ISO</th>
                        <th>Bunker?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.updated_ports_details.map((p, idx) => (
                        <tr key={`upd-${idx}`}>
                          <td>{p.row}</td>
                          <td>{p.unlocode}</td>
                          <td>{p.port_name}</td>
                          <td>{p.country_name}</td>
                          <td>{p.country_iso_code}</td>
                          <td>{p.is_bunker_port ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {results.errors && results.errors.length > 0 && (
              <div className="bulk-upload-details-section bulk-upload-error-section">
                <h3 className="bulk-upload-section-header bulk-upload-error-header">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> Errors ({results.errors.length})
                </h3>
                <div className="bulk-upload-table-wrapper">
                  <table className="bulk-upload-details-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>UN/LOCODE</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.errors.map((err, idx) => (
                        <tr key={`err-${idx}`}>
                          <td>{err.row}</td>
                          <td>{err.unlocode}</td>
                          <td className="bulk-upload-error-message">{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="bulk-upload-help-text">
                  <FontAwesomeIcon icon={faInfoCircle} /> Fix the errors in your CSV and re-upload to retry these rows.
                </p>
              </div>
            )}

            {(!results.errors || results.errors.length === 0)
              && (!results.new_ports_details || results.new_ports_details.length === 0)
              && (!results.updated_ports_details || results.updated_ports_details.length === 0) && (
              <div className="bulk-upload-empty-message">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>No rows were processed. Please check your CSV file.</span>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setResults(null);
              }}
            >
              Upload Another
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-modal-overlay" onClick={handleClose}>
      <div
        className="modal bulk-upload-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faAnchor} /> Bulk Upload Ports
          </h2>
          <button className="close-btn" onClick={handleClose} disabled={isUploading}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="bulk-upload-instructions">
            <h3>CSV Format Requirements</h3>
            <p>Your CSV file must include the following columns (any order, semicolon or comma delimited):</p>
            <ul style={{ fontSize: '0.9em', lineHeight: '1.6' }}>
              <li><strong>Port Name</strong> — required, max 100 chars</li>
              <li><strong>UN/LOCODE</strong> — required, max 10 chars, unique</li>
              <li><strong>Country Name</strong> — required, max 100 chars</li>
              <li><strong>Country ISO Code</strong> — required, max 10 chars (e.g. SG, NL, US)</li>
              <li><strong>Latitude</strong> — optional, -90 to 90</li>
              <li><strong>Longitude</strong> — optional, -180 to 180</li>
              <li><strong>Is Bunker Port</strong> — optional, true/false (default false)</li>
            </ul>
            <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
              <p style={{ margin: '0', fontSize: '0.9em' }}>
                <FontAwesomeIcon icon={faInfoCircle} />{' '}
                <strong>Upsert behaviour:</strong> rows matching an existing UN/LOCODE will UPDATE the port; new UN/LOCODE values will CREATE a new port.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="btn btn-secondary"
              style={{ marginTop: '12px', width: '100%' }}
            >
              <FontAwesomeIcon icon={faDownload} /> Download CSV Template
            </button>
          </div>

          <div className="form-group">
            <label>Select CSV File *</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files[0] || null)}
              className="form-input"
              disabled={isUploading}
              required
            />
            {file && (
              <div style={{ marginTop: '10px', padding: '8px', background: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                <p style={{ margin: '0', fontSize: '0.9em' }}>
                  <FontAwesomeIcon icon={faFileUpload} /> Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
                </p>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!file || isUploading}
            >
              {isUploading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Uploading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUpload} /> Upload CSV
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PortBulkUploadModal;
